import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Plus, X } from 'lucide-react'
import { catalogApi, type ClientDetail, type Tenant, type TenantInput } from '@/api/catalogApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field, NativeSelect } from '@/components/common/Field'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormError, ListState, StatusPill, TextAction, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { useCatalogMutation, useCatalogTenants } from '@/hooks/useCatalog'
import { codeFromName, emptyTenantForm, tenantCreatePayload, tenantPayload, tenantToForm, validateTenant, type TenantForm } from '@/lib/catalogForms'

/** A client's tenants (branches / sites) with their environment links. */
export function TenantsPanel({ client }: { client: ClientDetail }) {
  const { t } = useTranslation()
  const tenants = useCatalogTenants(client.id)
  const [editing, setEditing] = useState<Tenant | 'new' | null>(null)
  const [confirm, setConfirm] = useState<{ tenant: Tenant; action: 'retire' | 'delete' } | null>(null)
  const save = useCatalogMutation(({ id, input }: { id?: string; input: TenantInput }) =>
    id ? catalogApi.updateTenant(client.id, id, input) : catalogApi.createTenant(client.id, input),
  )
  const act = useCatalogMutation(({ tenant, action }: { tenant: Tenant; action: 'retire' | 'delete' | 'reactivate' }) =>
    action === 'delete'
      ? catalogApi.removeTenant(client.id, tenant.id).then(() => null)
      : catalogApi.updateTenant(client.id, tenant.id, { status: action === 'retire' ? 'retired' : 'active' }),
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('catalog.tenants.add')}</Button>
      </div>
      {act.error && !confirm ? <FormError error={act.error} /> : null}
      <ListState query={tenants}>
        {(tenants.data ?? []).length === 0 ? (
          <EmptyState compact icon={MapPin} message={t('catalog.tenants.empty')} />
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={`${thClass} w-44`}>{t('catalog.tenants.cols.name')}</th>
                <th className={`${thClass} w-20`}>{t('catalog.tenants.cols.code')}</th>
                <th className={`${thClass} w-40`}>{t('catalog.tenants.cols.timeZone')}</th>
                <th className={thClass}>{t('catalog.tenants.cols.location')}</th>
                <th className={thClass}>{t('catalog.tenants.cols.environments')}</th>
                <th className={`${thClass} w-24`}>{t('catalog.tenants.cols.status')}</th>
                <th className={`${thClass} w-[180px]`} />
              </tr>
            </thead>
            <tbody>
              {(tenants.data ?? []).map((tn) => {
                const refs = tn._count.tickets + tn._count.contacts + tn._count.users
                return (
                  <tr key={tn.id} className="border-b border-line">
                    <td className={`${tdClass} font-medium text-ink-strong`}>{tn.name}</td>
                    <td className={`${tdClass} font-mono`}>{tn.code}</td>
                    <td className={tdClass}>{tn.timeZone}</td>
                    <td className={tdClass}>{tn.location ?? '—'}</td>
                    <td className={tdClass}>
                      {tn.environments.length === 0 ? (
                        '—'
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {tn.environments.map((l) => (
                            <li key={l.environmentId} className="flex flex-wrap items-baseline gap-x-1.5">
                              <span className="text-ink-strong">
                                {l.environment.product.name} · {l.environment.name}
                              </span>
                              {l.externalTenantId ? <span className="font-mono text-label text-ink-muted">{l.externalTenantId}</span> : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className={tdClass}>
                      <StatusPill status={tn.status} />
                    </td>
                    <td className={tdClass}>
                      <div className="flex justify-end gap-3">
                        <TextAction onClick={() => setEditing(tn)}>{t('common.edit')}</TextAction>
                        {tn.status === 'active' ? (
                          <TextAction onClick={() => setConfirm({ tenant: tn, action: 'retire' })}>{t('catalog.retire')}</TextAction>
                        ) : (
                          <TextAction onClick={() => act.mutate({ tenant: tn, action: 'reactivate' })}>{t('catalog.reactivate')}</TextAction>
                        )}
                        <TextAction danger disabled={refs > 0} onClick={() => setConfirm({ tenant: tn, action: 'delete' })}>
                          {t('common.delete')}
                        </TextAction>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </ListState>

      {editing ? (
        <TenantDialog
          client={client}
          tenant={editing === 'new' ? null : editing}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) => save.mutate({ id: editing === 'new' ? undefined : editing.id, input }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <ConfirmDialog
        open={confirm !== null}
        destructive={confirm?.action === 'delete'}
        title={t(confirm?.action === 'delete' ? 'catalog.tenants.deleteTitle' : 'catalog.tenants.retireTitle', { name: confirm?.tenant.name ?? '' })}
        body={t(confirm?.action === 'delete' ? 'catalog.tenants.deleteBody' : 'catalog.tenants.retireBody')}
        confirmLabel={confirm?.action === 'delete' ? t('common.delete') : t('catalog.retire')}
        pending={act.isPending}
        error={act.error ? errorMessage(act.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setConfirm(null)
            act.reset()
          }
        }}
        onConfirm={() => confirm && act.mutate(confirm, { onSuccess: () => setConfirm(null) })}
      />
    </div>
  )
}

function TenantDialog({
  client,
  tenant,
  pending,
  error,
  onClose,
  onSave,
}: {
  client: ClientDetail
  tenant: Tenant | null
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: TenantInput) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<TenantForm>(() => (tenant ? tenantToForm(tenant) : emptyTenantForm(client.timeZone)))
  const [codeTouched, setCodeTouched] = useState(!!tenant)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)
  const linked = new Set(tenant?.environments.map((l) => l.environmentId) ?? [])
  // Active environments, plus retired ones this tenant already links (kept, not newly linkable).
  const envOptions = client.environments.filter((e) => e.status === 'active' || linked.has(e.id))

  function submit(e: FormEvent) {
    e.preventDefault()
    const found = validateTenant(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(tenant ? tenantPayload(form) : tenantCreatePayload(form))
  }

  const setLink = (i: number, patch: Partial<TenantForm['links'][number]>) =>
    setForm({ ...form, links: form.links.map((l, j) => (j === i ? { ...l, ...patch } : l)) })

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tenant ? t('catalog.tenants.editTitle') : t('catalog.tenants.createTitle')}</DialogTitle>
        </DialogHeader>
        <form className="grid grid-cols-[1fr_160px] gap-4" onSubmit={submit} noValidate>
          <Field id="tn-name" label={t('catalog.tenants.name')} error={err('name')}>
            <Input
              id="tn-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, ...(codeTouched ? {} : { code: codeFromName(e.target.value) }) })}
            />
          </Field>
          <Field id="tn-code" label={t('catalog.tenants.code')} error={err('code')}>
            <Input
              id="tn-code"
              className="font-mono uppercase"
              value={form.code}
              onChange={(e) => {
                setCodeTouched(true)
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }}
            />
          </Field>
          <p className="col-span-2 -mt-2 text-label text-ink-muted">{t('catalog.tenants.codeHelp')}</p>
          <Field id="tn-tz" label={t('catalog.tenants.timeZone')} error={err('timeZone')}>
            <Input id="tn-tz" value={form.timeZone} onChange={(e) => setForm({ ...form, timeZone: e.target.value })} />
          </Field>
          <div />
          <Field id="tn-loc" label={t('catalog.tenants.location')} className="col-span-2">
            <Input id="tn-loc" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </Field>

          <fieldset className="col-span-2 flex flex-col gap-2">
            <legend className="text-meta font-medium text-ink">{t('catalog.tenants.links')}</legend>
            <p className="text-label text-ink-muted">{t('catalog.tenants.linksHelp')}</p>
            {client.environments.length === 0 ? (
              <p className="text-meta text-ink-muted">{t('catalog.tenants.noEnvironments')}</p>
            ) : (
              <>
                {form.links.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                    <NativeSelect
                      aria-label={t('catalog.tenants.environment')}
                      value={l.environmentId}
                      onChange={(e) => setLink(i, { environmentId: e.target.value })}
                    >
                      <option value="">{t('common.select')}</option>
                      {envOptions.map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.product.name} · {env.name}
                          {env.status === 'retired' ? ` (${t('catalog.status.retired')})` : ''}
                        </option>
                      ))}
                    </NativeSelect>
                    <Input
                      aria-label={t('catalog.tenants.externalTenantId')}
                      className="font-mono"
                      placeholder={t('catalog.tenants.externalTenantId')}
                      value={l.externalTenantId}
                      onChange={(e) => setLink(i, { externalTenantId: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('catalog.tenants.removeLink')}
                      onClick={() => setForm({ ...form, links: form.links.filter((_, j) => j !== i) })}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  disabled={form.links.length >= envOptions.length}
                  onClick={() => {
                    const used = new Set(form.links.map((x) => x.environmentId))
                    const next = envOptions.find((env) => env.status === 'active' && !used.has(env.id))
                    setForm({ ...form, links: [...form.links, { environmentId: next?.id ?? '', externalTenantId: '' }] })
                  }}
                >
                  <Plus className="mr-1 size-4" />
                  {t('catalog.tenants.addLink')}
                </Button>
              </>
            )}
            {err('links') ? <p className="text-label text-status-escalated-fg">{err('links')}</p> : null}
          </fieldset>

          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
